<template>
  <div class="w-full max-w-2xl" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="space-y-4">
      <div
        v-for="item in scanItems"
        :key="item.id"
        class="p-4 rounded-2xl border border-blue-100 dark:border-blue-900/50 bg-white/90 dark:bg-slate-900/70 shadow-sm"
      >
        <div class="flex items-start justify-between" :class="isRtl === true ? 'flex-row-reverse' : 'flex-row'">
          <div class="flex items-start min-w-0 flex-1" :class="isRtl === true ? 'flex-row-reverse' : 'flex-row'">
            <span class="w-9 h-9 rounded-lg bg-blue-50 dark:bg-slate-900 border border-blue-100 dark:border-blue-800/60 flex items-center justify-center flex-shrink-0" :class="isRtl === true ? 'ml-3' : 'mr-3'">
              <Icon :icon="resolveTypeIcon(item.type)" width="18" class="text-blue-500 dark:text-blue-300" />
            </span>
            <div class="min-w-0 flex-1" :class="isRtl === true ? 'text-right' : 'text-left'">
              <div class="font-medium text-slate-800 dark:text-slate-100 truncate">{{ item.label }}</div>
              <div class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{{ item.detail }}</div>
            </div>
          </div>
          <span class="text-xs font-medium px-2 py-1 rounded-full flex-shrink-0" :class="resolveStatusClass(item.phase)">
            {{ resolveStatusLabel(item) }}
          </span>
        </div>
        <div v-if="item.phase === 'starting'" class="mt-3 text-sm text-slate-600 dark:text-slate-400">
          {{ t('agentCreate.knowledge.processing.starting', 'Starting scan...') }}
        </div>
        <div v-else-if="item.phase === 'scanning' || item.phase === 'done'" class="mt-3">
          <div class="flex items-center justify-between text-sm mb-1.5">
            <span class="text-slate-600 dark:text-slate-400">
              {{ t('agentCreate.knowledge.processing.pages', '{scanned} / {total} pages', { scanned: item.scannedPages, total: item.totalPages }) }}
            </span>
            <span class="font-medium text-blue-600 dark:text-blue-300">{{ item.percent }}%</span>
          </div>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div class="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300 ease-out" :style="{ width: item.percent + '%' }"></div>
          </div>
        </div>
        <div v-else-if="item.phase === 'failed'" class="mt-3 text-sm text-red-600 dark:text-red-400">
          {{ t('agentCreate.knowledge.processing.failed', 'Could not start scanning this source.') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';

const props = defineProps({
  sources: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['complete']);

const { t, currentLanguage, availableLanguages } = useI18n();

const scanItems = ref([]);
let scanTimers = [];

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

function resolveTypeIcon(type) {
  if (type === 'website') {
    return 'tabler:world-www';
  }
  if (type === 'urls') {
    return 'tabler:link';
  }
  return 'tabler:file-upload';
}

function resolveTotalPages(source) {
  if (source.type === 'website') {
    return 24 + Math.floor(Math.random() * 40);
  }
  if (source.type === 'urls') {
    if (Array.isArray(source.urls) === true && source.urls.length > 0) {
      return source.urls.length;
    }
    return 1;
  }
  if (Array.isArray(source.fileNames) === true && source.fileNames.length > 0) {
    return source.fileNames.length;
  }
  return 1;
}

function resolveStatusClass(phase) {
  if (phase === 'done') {
    return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  }
  if (phase === 'failed') {
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  }
  if (phase === 'scanning') {
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  }
  return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
}

function resolveStatusLabel(item) {
  if (item.phase === 'done') {
    return t('agentCreate.knowledge.processing.statusDone', 'Done');
  }
  if (item.phase === 'failed') {
    return t('agentCreate.knowledge.processing.statusFailed', 'Failed');
  }
  if (item.phase === 'scanning') {
    return t('agentCreate.knowledge.processing.statusScanning', 'Scanning');
  }
  return t('agentCreate.knowledge.processing.statusStarting', 'Starting');
}

function buildScanItems() {
  const items = [];
  let index = 0;
  while (index < props.sources.length) {
    const source = props.sources[index];
    items.push({
      id: source.id,
      type: source.type,
      label: source.label,
      detail: source.detail,
      phase: 'pending',
      totalPages: resolveTotalPages(source),
      scannedPages: 0,
      percent: 0
    });
    index = index + 1;
  }
  return items;
}

function allItemsFinished() {
  let index = 0;
  while (index < scanItems.value.length) {
    const item = scanItems.value[index];
    if (item.phase !== 'done' && item.phase !== 'failed') {
      return false;
    }
    index = index + 1;
  }
  return scanItems.value.length > 0;
}

function tryComplete() {
  if (allItemsFinished() === true) {
    window.setTimeout(() => {
      emit('complete');
    }, 900);
  }
}

function startItemScan(itemIndex) {
  const item = scanItems.value[itemIndex];
  if (item === undefined || item === null) {
    return;
  }
  item.phase = 'starting';
  const startDelay = 600 + Math.floor(Math.random() * 500);
  const startTimer = window.setTimeout(() => {
    if (Math.random() < 0.04) {
      item.phase = 'failed';
      tryComplete();
      return;
    }
    item.phase = 'scanning';
    const scanTimer = window.setInterval(() => {
      if (item.scannedPages < item.totalPages) {
        item.scannedPages = item.scannedPages + 1;
        item.percent = Math.round((item.scannedPages / item.totalPages) * 100);
      } else {
        window.clearInterval(scanTimer);
        item.phase = 'done';
        item.percent = 100;
        tryComplete();
      }
    }, 280 + Math.floor(Math.random() * 220));
    scanTimers = scanTimers.concat([scanTimer]);
  }, startDelay);
  scanTimers = scanTimers.concat([startTimer]);
}

onMounted(() => {
  scanItems.value = buildScanItems();
  let index = 0;
  while (index < scanItems.value.length) {
    const delay = index * 400;
    const queueTimer = window.setTimeout(() => {
      startItemScan(index);
    }, delay);
    scanTimers = scanTimers.concat([queueTimer]);
    index = index + 1;
  }
});

onBeforeUnmount(() => {
  let index = 0;
  while (index < scanTimers.length) {
    window.clearInterval(scanTimers[index]);
    window.clearTimeout(scanTimers[index]);
    index = index + 1;
  }
  scanTimers = [];
});
</script>
